import { Add, AdminPanelSettingsRounded, ArrowBack, DeleteOutline } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  ListSubheader,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/main/main_layout";
import Loading from "../../../../components/loading/loading";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { useQuery } from "react-query";
import { queryClient } from "../../../../providers/query/query";
import { greyEnabled, orange, purple, purpleLight, black } from "../../../../theme/colors";
import { roles } from "../../role_management/role_management";
import { getArea, getBrand, getNeedsFilters } from "../../../../services/needs/needs_service";
import { NeedFilters, Option } from "../../../needs/model/needs_filters";
import { getCityMasterData, getStateMasterData } from "../../../../services/master/master_service";
import MapChoicesPopup from "../../../surveyor/components/map_choices_popup/map_choices_popup";
import { Choices } from "../../../choices/model/choices";
import dayjs from "dayjs";
import { Scheme } from "../../../schemes/models/schemes";
import MapSchemePopup from "../map_schemes_popup/map_schemes_popup";
import { ClientPermission, createRoles, getRolesDetails, updateRoles } from "../../../../services/client_mapping/client_mapping";
import { getModulesCatalog } from "../../../../services/organizations/organizations";
import { OrganizationModule } from "../../../organizations/model/organization";
import { manage_roles_and_access } from "../../../../routes/routes_path";
import { RoletDetails } from "../../model/role_details";
import useUser from "../../../../providers/user/user_provider";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { DEFAULT_ORG_ID } from "../../../../constants";
import PermissionCard from "./permission_card";
import { iconsConstants } from "../../constants/contants";
export interface PermissionLabel {
  id: number;
  name: string;
}

type PermissionAction = "read" | "create" | "update" | "delete";
export interface SelectedRoleDetails {
  featureId: number;
  permission: number[];
}

export const access_permissions: string[] = ['create', 'update', 'delete', 'read'];

// The Casbin module for surveys/choices is 'choices' (see platform.modules'
// seed row: key 'choices', name 'Surveys'), but every bit of permission
// logic below (permissionComp, the save-button validation, iconsConstants)
// is already built around the UI key 'surveys' — this is the one spot that
// bridges the catalog's key to that existing UI key, instead of touching
// every one of those call sites.
function toUiModuleKey(catalogKey: string): string {
  return catalogKey === 'choices' ? 'surveys' : catalogKey;
}

export default function AddAdminRole({ modifyRole, viewRole = false }:
  { modifyRole: boolean, viewRole?: boolean }) {
  const [roleName, setRoleName] = useState("");
  const [clientEmailId, setClientEmailId] = useState("");
  const { showSnackbar } = useFeedback();
  const navigate = useNavigate();
  const [_categoryId, _setCategoryId] = useState<number | null>(null);
  const [_subCategoryId, _setSubcategoryId] = useState<number | null>(null);
  const [_brand, _setBrandId] = useState<number | null>(null);
  const [_areaId, _setAreaId] = useState<number[] | null>(null);
  const [brands, setBrands] = useState([]);
  const [areas, setAreas] = useState([]);
  const [_stateId, _setStateId] = useState<number | null>(null);
  const [_city, _setCity] = useState<number[] | null>(null);
  const [accessType, setAccessType] = useState<string[]>([])
  const [moduleAccess, setModuleAccess] = useState<string>('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openSchemeModal, setOpenSchemeModal] = useState(false);
  const [selectedChoicesList, setSelectedChoicesList] = useState<
    Choices[] | []
  >([]);
  const [selectedSchemeList, setSelectedSchemesList] = useState<
    Scheme[] | []
  >([]);
  const [error, setError] = useState(false);
  const [superAdmin, setSuperAdmin] = useState(false)
  const [submitting, setSubmitting] = useState(false);

  const [actionByModule, setActionByModule] = useState<
    Record<string, PermissionAction[]>
  >({}); const { id } = useParams();
  const { userPermissions } = useUser();
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: roleDetails, isLoading: isRoleDetailsLoading } = useQuery<RoletDetails | null>(
    ["get-roles-details", id],
    async () => {
      let response = await getRolesDetails({ roleId: +id! });
      return response.data?.data;
    }, {
    enabled: !!id
  }
  );

  // The org this role is being created for (or, on edit/view, belongs to).
  // A super-admin picking an org below creates the role there; otherwise
  // falls back to the caller's own org, matching add_new_client.tsx.
  const targetOrgId = id
    ? roleDetails?.orgId ?? null
    : selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;

  // Scoped to the target org's actual module subscriptions — a role can
  // only be granted access to modules that org has subscribed to. On
  // create this resolves immediately (targetOrgId always has a value via
  // the DEFAULT_ORG_ID fallback); on edit/view it waits for roleDetails to
  // supply the role's own orgId.
  const { data: modulesCatalog, isLoading: isModulesCatalogLoading } = useQuery(
    ["organization-modules-catalog", targetOrgId],
    async () => {
      let response = await getModulesCatalog(targetOrgId);
      return (response?.data?.data?.list || []) as OrganizationModule[];
    },
    { enabled: !!targetOrgId }
  );

  const moduleOptions = (modulesCatalog || []).map((module) => ({
    key: toUiModuleKey(module.key),
    label: module.name,
  }));

  useEffect(() => {
    if (modifyRole || viewRole) {
      console.log("updating modules based on role details",id, roleDetails);

      setRoleName(roleDetails?.roleAssigned || '')
      setAccessType(roleDetails?.actions || []);
      _setCategoryId(+roleDetails?.category?.id! || null);
      _setAreaId(roleDetails?.area?.map((i) => i?.id) || null);

      _setSubcategoryId(+roleDetails?.subcategory?.id!)
      _setStateId(+roleDetails?.state?.id!)
      _setCity(roleDetails?.city?.map((i) => i?.id) || null)
      setSelectedSchemesList(roleDetails?.schemeDetails?.map((scheme: Scheme) => {
        return { ...scheme, schemeId: scheme?.id }
      }) || []);
      setModuleAccess(roleDetails?.module[0] || '');
      setSelectedModules((roleDetails?.module?.map((mod) => mod == 'choices' ? 'surveys' : mod) || []).map((m: string) => m?.toLowerCase()));
      setClientEmailId(roleDetails?.clientEmail || "")
      setSelectedChoicesList(roleDetails?.choiceDetails || []);
    } else {
      let filter = roles?.filter((i) => i.moduleName?.toLowerCase() === roleDetails?.module[0]);
      setModuleAccess(filter[0]?.moduleName)
      if (filter?.[0]?.moduleName) {
        setSelectedModules([filter[0].moduleName.toLowerCase()]);
      }
    }
    console.log("role details", roleDetails);

    setSuperAdmin(roleDetails?.isSuperAdmin || false)
  }, [roleDetails,id])

  // Keep single-module state in sync with multi-select
  useEffect(() => {
    if (!selectedModules?.length) {
      setModuleAccess('');
      return;
    }
    if (!selectedModules.includes(moduleAccess?.toLowerCase())) {
      setModuleAccess(selectedModules[0]);
    }
  }, [selectedModules, moduleAccess])




  function permissionBody(): ClientPermission[] {
    console.log(actionByModule, "actionByModule");
    console.log(selectedModules, "selectedModules");
    const permissions: ClientPermission[] = [];

    (selectedModules || []).forEach((moduleKey) => {
      const m = moduleKey.toLowerCase();
      if (m === 'needs') {
        if (_categoryId !== 1) {
          permissions.push({
            module: 'needs',
            actions: actionByModule['needs'] || [],
            filters: {
              category_id: [_categoryId],
              subcategory_id: [_subCategoryId],
              brand_id: [_brand!],
              areas_ids: _areaId,
              state_ids: [_stateId!],
              city_ids: _city!,
            },
          });
        } else {
          permissions.push({
            module: 'needs',
            actions: actionByModule['needs'] || [],
            filters: {
              category_id: [_categoryId],
              subcategory_id: [_subCategoryId],
              areas_ids: _areaId,
              state_ids: [_stateId!],
              city_ids: _city!,
            },
          });
        }
      } else if (m === 'schemes') {
        permissions.push({
          module: 'schemes',
          actions: actionByModule['schemes'] || [],
          filters: { scheme_ids: selectedSchemeList.map((i) => i.schemeId) },
        });
      } else if (m === 'surveys') {
        permissions.push({
          module: 'choices',
          actions: actionByModule['surveys'] || [],
          filters: { choice_ids: selectedChoicesList.map((i) => i.id) },
        });
      } else if (m === 'services') {
        permissions.push({
          module: 'services',
          actions: actionByModule['services'] || [],
          filters: { client_email: clientEmailId },
        });
      } else if (m === 'notifications') {
        permissions.push({ module: 'notifications', actions: actionByModule['notifications'] || [], filters: {} });
      } else if (m === 'merchant') {
        permissions.push({ module: 'merchant', actions: actionByModule['merchant'] || [], filters: {} });
      } else if (m === 'jobs') {
        permissions.push({
          module: 'jobs',
          actions: actionByModule['jobs'] || [],
          filters: { client_email: clientEmailId },
        });
      }
      else if (m == 'landing-page') {
        permissions.push({ module: 'landing-page', actions: actionByModule['landing-page'] || [], filters: {} });
      }
      else {
        // Default: modules without specific filters
        permissions.push({ module: m, actions: actionByModule[m] || [], filters: {} });
      }
    });

    return permissions;
  }

  function extractErrorMessage(e: any): string {
    const rawMessage = e?.response?.data?.error?.message;
    if (!rawMessage) return 'Something went wrong';
    try {
      const parsed = JSON.parse(rawMessage);
      return parsed?.details?.[0]?.message || parsed?.message || rawMessage;
    } catch {
      return rawMessage;
    }
  }

  function createRolesApiCall() {
    const anyEmpty = Object.values(actionByModule).every(value => value.length > 0) && Object.keys(actionByModule).length > 0;
    if (!anyEmpty && !superAdmin) {
      showSnackbar('Please select at least one action for each module');
      return;
    }
    setSubmitting(true);
    createRoles({ name: roleName, permissions: superAdmin ? [] : permissionBody(), superAdmin: superAdmin, orgId: targetOrgId })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar('Role added successfully')
          queryClient.invalidateQueries({ queryKey: ["get-client-Roles"] });
          // Role pickers elsewhere (Add New Client / Modify Client) cache
          // the role list separately — without this, a newly created role
          // stays invisible there until something forces a refetch.
          queryClient.invalidateQueries({ queryKey: ["get-Roles-add-clients"] });
          queryClient.invalidateQueries({ queryKey: ["get-Roles"] });
          navigate(manage_roles_and_access)
        }
      })
      .catch((e) => {
        showSnackbar(extractErrorMessage(e));
      })
      .finally(() => setSubmitting(false));
  }

  function updateRolesApiCall() {
    setSubmitting(true);
    updateRoles({ name: roleName, permissions: superAdmin ? [] : permissionBody(), superAdmin: superAdmin, roleId: +id!, orgId: targetOrgId })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar('Role modified successfully')
          queryClient.invalidateQueries({ queryKey: ["get-client-Roles"] });
          queryClient.invalidateQueries({ queryKey: ["get-Roles-add-clients"] });
          queryClient.invalidateQueries({ queryKey: ["get-Roles"] });
          navigate(manage_roles_and_access)
        }
      })
      .catch((e) => {
        showSnackbar(extractErrorMessage(e));
      })
      .finally(() => setSubmitting(false));
  }

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
    disabled,
    onClear
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
    disabled?: boolean;
    onClear: () => void
  }) {
    return (
      <Box mr={4}>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            disabled={disabled}
            fullWidth
            // multiple
            value={
              selectedId == null
                ? { id: null, value: "" }
                : options.find((option) => selectedId === option?.id)
            }
            onChange={(e, value) => {
              onSelect(value?.id || null);
            }}
            options={options}
            getOptionLabel={(option) => option?.value}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label={placeholder} />
            )}

          />
        </Box>
      </Box>
    );
  }



  function _dropdownOptionMultiselect({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
    disabled,
    onClear
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[];
    selectedId: number[] | null;
    onSelect: (id: number[] | null) => void;
    disabled?: boolean;
    onClear: () => void
  }) {
    return (
      <Box mr={4}>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            disabled={disabled}
            fullWidth
            multiple
            options={(options || []).map(
              (item) => item.id
            )}
            disableCloseOnSelect
            getOptionLabel={(option) =>
              (options || []).find(
                (item) => item.id === option
              )?.value || ""
            }
            value={selectedId == null
              ? []
              : selectedId}
            onChange={(_, value, reason, details) => {
              if (
                value ||
                [].find((item) => item === details?.option)
              ) {
                let d = options?.filter(
                  (item: any) => {
                    return item.isAggregate === true;
                  }
                );
                if (value?.find((itm) => itm === d?.[0]?.id)) {
                  onSelect([d?.[0]?.id]);
                } else {
                  onSelect([...value]);
                }
              }
            }}
            renderOption={(
              props,
              option,
              { selected, index },
              { value }
            ) => {
              let isAllSelected: boolean = !!value?.find(
                (v) =>
                  // (options || []).find(
                  //   (item) => item.id === v
                  // )?.isAggregate ||
                  false
              );

              let optionDetails = (
                options || []
              ).find((item) => item.id === option);

              return (
                <>
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      value={index}
                    />
                    {optionDetails?.value}
                  </li>
                  {/* {optionDetails?.isAggregate ? <Divider /> : null} */}
                </>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${title}`}
                fullWidth
                placeholder={`select ${title}`}
              // error={!!errors.personalIncome}
              // helperText={errors.personalIncome?.message}
              />
            )}
          />
        </Box>
        {/* <Box display={'flex'} flexDirection={'row'}>
          {selectedId?.map((val) => {
            return <Box
              display={'flex'} flexDirection={'row'}
              sx={{ width: 'fit-content', borderRadius: 2 }}
              px={1}
              bgcolor='primary.main'
              alignItems={'center'}
              mr={1}
            >
              <IconButton onClick={onClear}>
                <Close sx={{ width: 17, height: 17, color: '#ffffff' }} />
              </IconButton><Typography color={'#ffffff'}   >
                {options.find((option) => val === option?.id)?.value}
              </Typography>
            </Box>
          })}
        </Box> */}
      </Box>
    );
  }
  const getAreaApiCall = () => {
    getArea({ categoryId: _categoryId, subcategoryId: _subCategoryId }).then(
      (res) => {
        setAreas(res.data.data.list);
      }
    );
  };

  const getGrandsApiCall = () => {
    getBrand({ categoryId: _categoryId, subcategoryId: _subCategoryId }).then(
      (res) => {
        setBrands(res.data.data.list);
      }
    );
  };

  useEffect(() => {
    if (modifyRole || viewRole) {
      setTimeout(() => {
        _setBrandId(+roleDetails?.brand?.[0]?.id!)
      }, 1000);
    }
  }, [brands])

  function getSubCategories(): Option[] {
    return _categoryId
      ? needsCategory?.categories?.find(
        (category) => category.category._id === _categoryId
      )?.subcategory || []
      : [];
  }

  const { data: needsCategory } = useQuery<NeedFilters>(
    ["needs-filters"],
    async () => {
      let data = await getNeedsFilters();
      return data?.data?.data;
    }
  );

  // Distinct key - the plain "masterDataList" key is shared by several
  // unrelated getMasterData() calls elsewhere (Users, Rewards, Schemes
  // enquiries) whose response shape is a {gender, state, ...} object, not
  // this endpoint's {list: [...]} shape. See users.tsx for the bug caused
  // by two different endpoints sharing one cache key.
  const { data: stateMaster } = useQuery(["clientMappingStateMasterList"], async () => {
    let response = await getStateMasterData();
    return response?.data?.data?.list;
  });

  const { data: cityMaster } = useQuery(["cityList", _stateId], async () => {
    if (_stateId) {
      let response = await getCityMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  useEffect(() => {
    if (_subCategoryId) {
      getGrandsApiCall();
      getAreaApiCall();
    }
  }, [_subCategoryId, _categoryId]);

  const handlePermissionChange = (data: any) => {
    console.log("Permission changed:", data);
    setActionByModule((prev) => ({
      ...prev,
      [data.module]: (Object.entries(data.permissions) as [
        keyof typeof data.permissions,
        boolean
      ][])
        .filter(([_, value]) => value)
        .map(([key]) => key),
    }));
  };


  // const { data: schemes } = useQuery(
  //   ["schemes", schemeSearchValue, page],
  //   async () => {
  //     let payload = {
  //       page: page + 1,
  //       size: 8,
  //       keyword: schemeSearchValue,
  //     };
  //     let response = await getSchemes(payload);
  //     return response.data.data;
  //   }
  // );


  function permissionComp() {
    const modulesToRender = (selectedModules && selectedModules.length > 0)
      ? selectedModules.map((m) => m.toLowerCase())
      : [moduleAccess?.toLowerCase()];

    const blocks: any[] = [];

    if (modulesToRender.includes('needs')) {
      blocks.push(<>
        <Box display={'flex'} flexDirection={'column'} mt={6}  >
          <Typography variant="subtitle1" fontWeight={600}>
            {'Needs Type'}
          </Typography>
          <Box display={'flex'} flexDirection={'row'}>
            {needsCategory?.categories?.map((cat, index) => {
              return needsCategory?.categories.length - 1 !== index &&
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                  <Radio
                    checked={cat.category._id === _categoryId}
                    onChange={() => {
                      if (!viewRole) {
                        _setCategoryId(cat.category._id)
                        // _setCategoryId(null);
                        _setSubcategoryId(null);
                        _setBrandId(null);
                        _setAreaId(null);
                        _setStateId(null);
                        _setCity(null);

                      }

                    }}
                  />
                  <Typography variant="subtitle1">
                    {cat.category.name}
                  </Typography>
                </Box>
            })}


          </Box>
        </Box>
        <Box mt={1}>
          {_dropdownOption({
            title:
              _categoryId === 2
                ? "Company"
                : "Category",
            placeholder:
              _categoryId === 2
                ? "Company"
                : "Category",
            options: getSubCategories()?.map((option) => {
              return { id: option._id, value: option.name };
            }),
            selectedId: _subCategoryId,
            onSelect: (id) => {
              if (!viewRole) {
                _setSubcategoryId(id);
                _setBrandId(null);
                _setAreaId(null);
              }
            },
            onClear: () => {
              _setSubcategoryId(null)
            },
          })}
        </Box>

        {_categoryId === 2 && (
          <Box mt={0}>
            {_dropdownOption({
              title: "Brand",
              placeholder: "Search for Brand",
              options:
                brands?.map((brand: { id: number, value: string }) => {
                  return { id: brand.id, value: brand.value }
                }) || [],
              selectedId: _brand,
              onSelect: (id) => {
                if (!viewRole)
                  _setBrandId(id);
              },
              onClear: () => {
                _setSubcategoryId(null)
              },
            })}
          </Box>
        )}
        <Box mt={0}>
          {_dropdownOptionMultiselect({
            title: "Area",
            placeholder: "Search for Area",
            options: areas || [],
            selectedId: _areaId,
            onSelect: (id) => {
              if (!viewRole)
                _setAreaId(id);
            },
            onClear: () => {
              _setSubcategoryId(null)
            },
          })}
        </Box>
        <Box mt={0}>
          {_dropdownOption({
            title: "State",
            placeholder: "Select State",
            selectedId: _stateId,
            onSelect: (id) => {
              _setStateId(id);
            },
            onClear: () => {
              _setStateId(null)
            },
            options:
              stateMaster?.map((state: { id: number, value: string }) => {
                return { id: state.id, value: state.value };
              }) || [],
          })}
        </Box>

        <Box mt={0}>
          {_dropdownOptionMultiselect({
            title: "City",
            placeholder: "Search for City",
            options: cityMaster,
            selectedId: _city,
            onSelect: (id) => {
              if (!viewRole)
                _setCity(id);
            },
            onClear: () => { }
          })}
        </Box>
      </>);
    }

    if (modulesToRender.includes('surveys')) {
      blocks.push(<Box>
        {selectedChoicesList?.length !== 0 && !viewRole && (
          <Box
            display={"flex"}
            mt={4}
            flexDirection={"row"}
            justifyContent={"space-between"}
          >
            <Typography variant="body2" fontWeight={"600"}>
              Surveys Mapped
            </Typography>
            <Button
              onClick={() => {
                setOpenModal(true);
              }}
            >
              <Typography
                variant="body2"
                fontWeight={"600"}
                color="primary.main"
              >
                Map more surveys
              </Typography>
            </Button>
          </Box>
        )}
        {selectedChoicesList?.length !== 0 ?
          selectedChoicesList?.map((choice: any) => {
            return <>
              <ListItem
                sx={{
                  margin: 0,
                  padding: 0,
                  mt: 3,
                }}
              >
                <ListItemText
                  primary={choice?.name}
                  secondary={
                    <Box display="flex" flexDirection="row">
                      <Typography variant="body2">{`Questions:${choice.totalQuestions}`}</Typography>
                      <Typography variant="body2" ml={3}>{`Valid from: ${choice.validFrom
                        ? dayjs(choice.validFrom, "YYYY-MM-DD").format(
                          "DD-MM-YYYY"
                        )
                        : "-"
                        }`}</Typography>
                      <Typography variant="body2" ml={3}>{`Valid to: ${choice.validTo
                        ? dayjs(choice.validTo, "YYYY-MM-DD").format(
                          "DD-MM-YYYY"
                        )
                        : "-"
                        }`}</Typography>

                    </Box>
                  }
                  primaryTypographyProps={{
                    color: black,
                    fontWeight: 600
                  }}
                  secondaryTypographyProps={{
                    color: "#91278F",
                  }}
                />
                <ListSubheader sx={{ margin: 0, padding: 0 }}>
                  <IconButton
                    style={{ marginLeft: 16 }}
                    onClick={() => {
                      if (!viewRole) {
                        let filteredList = selectedChoicesList.filter(
                          (item) => item?.id !== choice.id
                        );
                        setSelectedChoicesList(filteredList);
                      }
                    }}
                    sx={{
                      backgroundColor: "#E9F0ED",
                      borderRadius: 4,
                      color: black,
                    }}
                  >
                    <DeleteOutline></DeleteOutline>
                  </IconButton>
                </ListSubheader>
              </ListItem>
            </>
          })
          : (
            <Box
              display="flex"
              flexDirection={"column"}
              mt={3}
              borderRadius={5}
              sx={{ minHeight: 200, border: "1px dashed #91278F" }}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <IconButton onClick={() => setOpenModal(true)}>
                <Add sx={{ color: "#91278F" }} />
              </IconButton>
              <Button
                onClick={() => {
                  if (!viewRole) {
                    setOpenModal(true);
                  }
                }}
                sx={{ color: black, fontSize: 18 }}
              >
                Click here to map surveys
              </Button>
            </Box>
          )
        }
      </Box>);
    }

    if (modulesToRender.includes('schemes')) {
      blocks.push(<Box>
        {selectedSchemeList?.length !== 0 && (
          <Box
            display={"flex"}
            mt={4}
            flexDirection={"row"}
            justifyContent={"space-between"}
          >
            <Typography variant="body2" fontWeight={"600"}>
              Schemes Mapped
            </Typography>
            <Button
              onClick={() => {
                setOpenSchemeModal(true);
              }}
            >
              <Typography
                variant="body2"
                fontWeight={"600"}
                color="primary.main"
              >
                Map more schemes
              </Typography>
            </Button>
          </Box>
        )}
        {selectedSchemeList?.length !== 0 ? (
          selectedSchemeList?.map((item: Scheme) => {
            return (
              <>
                <ListItem
                  sx={{
                    margin: 0,
                    padding: 0,
                    mt: 3,
                  }}
                >
                  <ListItemText
                    primary={item?.name}
                    secondary={
                      <Box display="flex" flexDirection="row">
                        <Typography variant="body2">{item.governmentType}</Typography>
                        <Typography variant="body2">{item.schemeGovernmentType}</Typography>
                        <Box display={"flex"} flexDirection={'row'} mb={1}>
                          {item?.categories?.map((cat: string) => {
                            return (
                              <Typography
                                color="primary.main"
                                variant="body2"
                                textTransform={"capitalize"}
                                ml={1}
                                mr={1}
                              >
                                {cat}
                              </Typography>
                            );
                          })}
                          {item?.schemeCategory?.map((cat: string) => {
                            return (
                              <Typography
                                color="primary.main"
                                variant="body2"
                                textTransform={"capitalize"}
                                ml={1}
                                mr={1}
                              >
                                {cat}
                              </Typography>
                            );
                          })}
                        </Box>
                      </Box>
                    }
                    primaryTypographyProps={{
                      color: black,
                      fontWeight: 600
                    }}
                    secondaryTypographyProps={{
                      color: "#91278F",
                    }}
                  />
                  <ListSubheader sx={{ margin: 0, padding: 0 }}>
                    <IconButton
                      style={{ marginLeft: 16 }}
                      onClick={() => {
                        if (!viewRole) {
                          let filteredList = selectedSchemeList.filter(
                            (scheme) => item?.schemeId !== scheme?.schemeId
                          );
                          setSelectedSchemesList(filteredList);
                        }
                      }}
                      sx={{
                        backgroundColor: "#E9F0ED",
                        borderRadius: 4,
                        color: black,
                      }}
                    >
                      <DeleteOutline></DeleteOutline>
                    </IconButton>
                  </ListSubheader>
                </ListItem>
              </>
            );
          })
        ) : (
          <Box
            display="flex"
            flexDirection={"column"}
            mt={3}
            borderRadius={5}
            sx={{ minHeight: 200, border: "1px dashed #91278F" }}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <IconButton onClick={() => setOpenSchemeModal(true)}>
              <Add sx={{ color: "#91278F" }} />
            </IconButton>
            <Button
              onClick={() => {
                if (!viewRole) {
                  setOpenSchemeModal(true);
                }
              }}
              sx={{ color: black, fontSize: 18 }}
            >
              Click here to map Schemes
            </Button>
          </Box >
        )}
      </Box>);
    }

    return <>{blocks}</>;
  }


  return (
    <MainLayout>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <AdminPanelSettingsRounded sx={{ color: purple, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color={black}>
                {modifyRole ? "Modify Role" : viewRole ? "View Role Details" : "Add New Role"}
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                {viewRole ? "Review the module access granted to this role." : "Define the role name and the module access it grants."}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{ width: "100%", mt: 2, borderRadius: 3, border: "1px solid #ECECEC" }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            {(id && isRoleDetailsLoading) || isModulesCatalogLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : (
            <Box display="flex" flexDirection="column">
              {!id ? (
                <Box mt={0}>
                  <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
                </Box>
              ) : (
                <Box mt={0}>
                  <TextField
                    value={roleDetails?.orgName || ""}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Organisation"
                    disabled
                  />
                </Box>
              )}
              <Box mt={3}>
                <TextField
                  required
                  value={roleName}
                  onChange={(e) => { if (!viewRole) setRoleName(e.target.value) }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Role Name"
                  placeholder="Enter Role name"
                // disabled={viewRole}
                />
              </Box>
              {(!superAdmin && (selectedModules.includes('jobs') || selectedModules.includes('services'))
              )
                && <Box mt={2} >
                  <TextField
                    required
                    value={clientEmailId}
                    onChange={(e) => { if (!viewRole) setClientEmailId(e.target.value); setError(false) }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Client Email Id"
                    placeholder="Enter Client Email Id"
                  // disabled={viewRole}
                  />
                  {error && (
                    <FormHelperText sx={{ color: "red", m: 0 }}>
                      Please Enter valid Email
                    </FormHelperText>
                  )}
                </Box>}

              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} mt={2}>
                <Checkbox checked={superAdmin} onChange={() => { if (!viewRole) setSuperAdmin(!superAdmin) }} />
                <Typography variant="body1" fontWeight={600}>
                  Super Admin Role
                </Typography>
              </Box>


              {!superAdmin && <Box display={'flex'} flexDirection={'row'} mt={2}>
                <Typography variant="subtitle2">
                  Provide Access For
                </Typography>
                <Typography sx={{ color: orange }} variant="subtitle2">*</Typography>
              </Box>}
              {!superAdmin && <>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} mb={1}>
                  <Checkbox
                    checked={selectedModules.length === moduleOptions.length && moduleOptions.length > 0}
                    indeterminate={selectedModules.length > 0 && selectedModules.length < moduleOptions.length}
                    onChange={() => {
                      if (viewRole) return;
                      if (selectedModules.length === moduleOptions.length) {
                        setSelectedModules([]);
                        setActionByModule({});
                      } else {
                        setSelectedModules(moduleOptions.map((option) => option.key));
                        setActionByModule(() =>
                          moduleOptions.reduce((acc, option) => {
                            acc[option.key] = [] as PermissionAction[];
                            return acc;
                          }, {} as Record<string, PermissionAction[]>)
                        );
                      }
                    }}
                  />
                  <Typography variant="subtitle1">Select All</Typography>
                </Box>
                <Box display={'flex'} flexWrap={'wrap-reverse'} flexDirection={'row'}>
                  {
                    moduleOptions.map((option) => {
                      const key = option.key;
                      const checked = selectedModules.includes(key);
                      return <Box key={key} display={'flex'} flexDirection={'row'} mr={1} alignItems={'center'}>
                        <Checkbox
                          checked={checked}
                          onChange={() => {
                            if (viewRole) return;
                            if (checked) {
                              const next = selectedModules.filter((m) => m !== key);
                              setSelectedModules(next);
                              let updatedModules = { ...actionByModule };
                              delete updatedModules[key]
                              setActionByModule(updatedModules)
                            } else {
                              const next = [...selectedModules, key];
                              setSelectedModules(next);
                              setActionByModule((prev) => ({ ...prev, [key]: [] as PermissionAction[] }));
                              setModuleAccess(key);
                            }
                          }}
                        />
                        <Typography variant="subtitle1" onClick={() => { if (selectedModules.includes(key)) setModuleAccess(key); }} sx={{ cursor: 'pointer' }}>
                          {option.label}
                        </Typography>
                      </Box>
                    })
                  }
                </Box>
              </>}
              {!superAdmin && <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 300px))",
                    gap: 2,
                    justifyContent: "flex-start", // 🔥 prevents stretching
                  }}
                >
                  {selectedModules.map((module) => (
                    <PermissionCard
                      key={module}
                      isViewMode={roleDetails!=undefined && roleDetails!=null && !modifyRole}
                      moduleName={module}
                      onChange={handlePermissionChange}
                      icon={iconsConstants[module]}
                      roleDetails={roleDetails || null}
                    />
                  ))}
                </Box>
                <Divider
                  sx={{
                    display: "flex",
                    opacity: 0.5,
                    borderBottom: "1px dotted #7C797C",
                    mt: 4
                  }}
                />
                {permissionComp()}
              </>
              }
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                mt={5}
              >
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  sx={{
                    borderColor: purple,
                    color: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 150,
                    "&:hover": { borderColor: purple, bgcolor: purpleLight },
                  }}
                >
                  {viewRole ? "Back" : "Cancel"}
                </Button>
                {!viewRole && <Button
                  onClick={() => {
                    if (roleName) {
                      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
                      const needsEmail = selectedModules.includes('jobs') || selectedModules.includes('services');
                      if (needsEmail && !emailRegex?.test(clientEmailId)) {
                        setError(true);
                      } else {
                        if (modifyRole) {
                          updateRolesApiCall()
                        } else {
                          createRolesApiCall()
                        }
                      }
                    }
                  }}
                  disabled={submitting || (superAdmin ? !roleName : (
                    !roleName ||
                    selectedModules.length === 0 ||
                    ((selectedModules.includes('jobs') || selectedModules.includes('services')) && !clientEmailId) ||
                    (selectedModules.includes('schemes') ? !selectedSchemeList.length : false) ||
                    (selectedModules.includes('surveys') ? !selectedChoicesList.length : false) ||
                    (selectedModules.includes('needs') ? !_categoryId || !_subCategoryId || !_areaId || !_stateId || (_stateId!=1 && !_city?true:false) : false)
                  ))}
                  variant="contained"
                  disableElevation
                  startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                  sx={{
                    minWidth: 150,
                    bgcolor: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#7A2074" },
                  }}
                >
                  {modifyRole ? "Modify Role" : "Add Role"}
                </Button>}
              </Box>
            </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      {openModal && <MapChoicesPopup
        openModel={openModal}
        setOpenModal={setOpenModal}
        setSelectedChoicesList={(item) => {
          setSelectedChoicesList(item!);
          setOpenModal(false);
        }}
        selectedChoicesList={selectedChoicesList}
      />}
      {openSchemeModal &&
        <MapSchemePopup
          openModel={openSchemeModal}
          setOpenModal={setOpenSchemeModal}
          setSelectedSchemeList={(item) => {
            setSelectedSchemesList(item!);
            setOpenSchemeModal(false);
          }}
          selectedSchemeList={selectedSchemeList}
        />}
    </MainLayout>
  );
}
